import { Form, Link, redirect, useFetcher, useLoaderData } from "@remix-run/react";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { handleize, transformName } from "~/library/utilities";
import { useEffect, useState, useRef } from "react";
import { z } from "zod";
import { TextField } from "~/components/TextField";
import { TextArea } from "~/components/TextArea";
import { Button } from "~/components/Button";

import dayjs from 'dayjs';
import { useRootLoaderData } from "~/root";
import { Check } from "lucide-react";

const emailFormSchema = z.object({
  name: z.string().min(1, "Naam is verplicht").min(2, "Naam moet minimaal 2 tekens bevatten"),
  email: z.string().min(1, "E-mail is verplicht").email("Ongeldig e-mailadres"),
  phone: z.string().optional(),
  message: z.string().min(1, "Bericht is verplicht").min(10, "Bericht moet minimaal 10 tekens bevatten"),
});

export const meta: MetaFunction = () => {
  return [
    { title: "Welten" },
  ];
}

export const loader = async ({ params }: LoaderFunctionArgs) => {
  return {
    handle: params?.handle
  };
};

export default function Vacature() {
  const { handle }: any  = useLoaderData()
  const rootData: any = useRootLoaderData()
  const fetcher = useFetcher<{ success: string, message: string, data: any }>()
  const jobs = rootData?.jobs || []
  const formRef = useRef<HTMLFormElement>(null)
 

  const job = jobs.filter((item: any) => handleize(item?.job?.Name) === handle)[0]

  useEffect(() => {
    if (fetcher.data?.data.success === true) {
      if (formRef.current) {
        formRef.current.reset()
      }
    }
  }, [fetcher.data?.data.success])

  if (!job) return <div className="u-p-12">No job fount</div>

  
  return (
    <>
      <div className="u-px-12 sm:u-px-20 u-py-32 u-bg-blue">
        <div className="u-max-w-screen-xl u-mx-auto">
          <div className="u-text-white u-text-[1.6rem] u-flex u-gap-x-4">
            <Link to="/" className="u-text-white/60">Vacatures</Link>

            {job?.job?.Name && 
              <>
                <span className="u-text-pink u-font-black">/</span>
                <span>{job.job.Name}</span>
              </>
            }
          </div>

          <div className="u-mt-12">
            {job?.account?.Name &&
              <h2 className="u-text-pink u-uppercase u-font-bold u-text-[2.4rem] u-italic">{transformName(job?.account?.Name)}</h2>
            }

            {job?.job?.Name &&
              <div className="u-mt-8">
                <h1 className="u-text-white u-font-proxima-nova u-text-[3.4rem] u-leading-[3.8rem] sm:u-text-[5.2rem] sm:u-leading-[5.2rem] u-italic u-font-black u-max-w-7xl">
                  {job.job.Name}
                </h1>
              </div>
            }

            <ul className="u-mt-8 u-text-white u-flex u-flex-col">
              {job?.job?.byner__Job_location__c &&
                <li>{job?.job?.byner__Job_location__c}</li>
              }

              {job?.job?.Vakgebied__c &&
                <li>{job?.job?.Vakgebied__c}</li>
              }

              {job?.job?.Niveau__c &&
                <li>{job?.job?.Niveau__c}</li>
              }

              {job?.job?.byner__Job_type__c &&
                <li>{job?.job?.byner__Job_type__c}</li>
              }

              {job?.job?.CreatedDate &&
                <li>{dayjs(job?.job?.CreatedDate).format('DD-MM-YYYY')}</li>
              }
            </ul>
          </div>
        </div>
      </div>

      <div className="u-px-12 sm:u-px-20 u-py-32">
        <div className="u-max-w-screen-xl u-mx-auto">

          <h2 className="u-text-lg u-uppercase u-font-black u-tracking-wide u-italic">Wat je bij ons gaat <span className="u-text-pink">doen</span></h2>
          {job.jobDescription.byner__Job_description__c &&
            <div className="u-mt-4 job-content" dangerouslySetInnerHTML={{ __html: job.jobDescription.byner__Job_description__c }} />
          }


          <h2 className="u-text-lg u-uppercase u-font-black u-tracking-wide u-italic u-mt-8">Zo zijn wij een <span className="u-text-pink">match</span></h2>
          {job.jobDescription.byner__Job_requirements__c &&
            <div className="u-mt-4 job-content" dangerouslySetInnerHTML={{ __html: job.jobDescription.byner__Job_requirements__c }} />
          }

          <h2 className="u-text-lg u-uppercase u-font-black u-tracking-wide u-italic u-mt-8">Hier kom je <span className="u-text-pink">terecht</span></h2>
          {job.jobDescription.Hier_kom_je_terecht__c &&
            <div className="u-mt-4 job-content" dangerouslySetInnerHTML={{ __html: job.jobDescription.Hier_kom_je_terecht__c }} />
          }
        </div>
      </div>

      <div className="u-px-12 sm:u-px-20 u-py-32 sm:u-py-52 u-bg-blue">
        <div className="u-max-w-screen-xl u-mx-auto">
          <div className="u-mt-12">
            <div>
              <div className="u-relative">
                <div className="u-absolute u-top-1/2 u--translate-y-1/2 u-left-0 u--translate-x-32">
                  <svg width="124" height="160" viewBox="0 0 124 160" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 160H48.765L124 0H75.2267L0 160Z" fill="#F62990"/>
                  </svg>
                </div>

                <h3 className="u-text-white u-z-10 u-relative u-font-proxima-nova u-text-[3.4rem] u-leading-[3.8rem] sm:u-text-[5.2rem] sm:u-leading-[5.2rem] u-italic u-font-black u-max-w-7xl">
                  Geïnteresseerd<br/>
                  <span className="u-text-pink">in deze vacature?</span>
                </h3>
              </div>

              <div className="u-mt-8">
                <p className="u-text-white">Neem dan direct contact op door het formulier hieronder in te vullen.</p>
              </div>
            </div>

            <div className="u-mt-12">
              {fetcher.data?.data.success === true && (
                <div className="u-flex u-items-center u-gap-2 u-text-white u-text-sm u-mb-4">
                  <Check className="u-size-6 u-text-pink" /> <span className="u-text-pink">Bedankt</span> voor je inzending.
                </div>
              )}

              <fetcher.Form ref={formRef} method="post" action="/api/email" className="u-flex u-text-white u-flex-col u-gap-8 u-max-w-2xl">
                <TextField
                  name="vacature"
                  label="Vacature"
                  placeholder="Vacature"
                  defaultValue={job.job.Name}
                  className="u-hidden"
                  isReadOnly={true}
                />

                <input
                  name="honeyPot"
                  placeholder="Honeypot"
                  defaultValue=""
                  className="u-sr-only u-w-0 u-h-0 u-opacity-0 u-pointer-events-none"
                />

                <TextField
                  name="vacatureId"
                  label="Vacature ID"
                  placeholder="Vacature ID"
                  defaultValue={job.job.Id}
                  className="u-hidden"
                  isReadOnly={true}
                />

                <div className="u-flex u-flex-col sm:u-flex-row u-w-full sm:u-items-center u-justify-between u-gap-4">
                  <TextField
                    name="name"
                    label="Naam"
                    placeholder="Jouw naam"
                    errorMessage={"Naam is verplicht"}
                    isRequired
                  />
                  
                  <TextField
                    name="email"
                    label="E-mail"
                    type="email"
                    placeholder="E-mailadres"
                    errorMessage={"Ongeldig e-mailadres"}
                    isRequired
                  />
                </div>


                <Button
                  type="submit"
                  isDisabled={fetcher.state === "submitting"}
                  className="u-self-end u-bg-pink u-mt-4 sm:u-mt-0 u-text-white u-cursor-pointer u-text-sm u-px-6 u-py-2 u-min-h-10"
                >
                  Versturen
                </Button>
              </fetcher.Form>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}